import { getWalletAddress, getWalletProviderId } from '@/utils/auth-token';
import { getUserProfile } from '@/api/user';
import {
  ensurePrivyEmbeddedReady,
  extractPrivyTradeAddressFields,
  getPrivyEmbeddedSignerAddress,
  getPrivySessionUser,
  isPrivyEmbeddedLogin,
} from '@/utils/privy';
import { getWalletProviderById, normalizeWalletProviderId } from '@/utils/wallet-provider';

function normalizeAddress(value) {
  return String(value ?? '').trim();
}

function pickWalletFromProfile(profile) {
  if (!profile || typeof profile !== 'object') return '';
  const candidates = [
    profile.walletAddress,
    profile.wallet,
    profile.address,
    profile.ethAddress,
    profile.userAddress,
  ];
  for (const value of candidates) {
    const trimmed = normalizeAddress(value);
    if (trimmed) return trimmed;
  }
  return '';
}

async function readEthAccounts(provider) {
  if (!provider?.request) return [];
  try {
    const accounts = await provider.request({ method: 'eth_accounts' });
    return (accounts || []).map((item) => normalizeAddress(item)).filter(Boolean);
  } catch (err) {
    console.warn('[wallet-address] eth_accounts failed', err);
    return [];
  }
}

function pushUniqueAddress(list, value) {
  const normalized = normalizeAddress(value);
  if (!normalized) return;
  const key = normalized.toLowerCase();
  if (list.some((item) => item.toLowerCase() === key)) return;
  list.push(normalized);
}

/**
 * Privy 可能绑定的地址（signer 优先；与缓存不一致时都查一遍余额）
 */
export async function resolvePrivyLoginWalletCandidates() {
  await ensurePrivyEmbeddedReady();

  const candidates = [];

  try {
    pushUniqueAddress(candidates, await getPrivyEmbeddedSignerAddress());
  } catch (err) {
    console.warn('[wallet-address] privy signer failed', err);
  }

  try {
    const user = await getPrivySessionUser();
    pushUniqueAddress(candidates, extractPrivyTradeAddressFields(user).address);
  } catch (err) {
    console.warn('[wallet-address] privy session user failed', err);
  }

  pushUniqueAddress(candidates, getWalletAddress());

  if (!candidates.length) {
    throw new Error('Privy embedded wallet address not found');
  }

  return candidates;
}

/** @deprecated 使用 resolvePrivyLoginWalletCandidates */
export async function resolvePrivyLoginWalletAddress() {
  const candidates = await resolvePrivyLoginWalletCandidates();
  return candidates[0];
}

/**
 * 按当前登录方式解析应查询的钱包地址。
 * - Google / 邮箱（Privy）→ 嵌入式 signer 及关联地址（去重）
 * - MetaMask / OKX 等 → 对应插件当前连接地址
 */
export async function resolveLoginWalletTargets() {
  const loginProvider = normalizeWalletProviderId(getWalletProviderId());

  if (isPrivyEmbeddedLogin()) {
    try {
      const candidates = await resolvePrivyLoginWalletCandidates();
      return candidates.map((address) => ({
        address,
        providerId: 'privy',
        addressType: 2,
      }));
    } catch (err) {
      console.warn('[wallet-address] privy wallet resolve failed', err);
      return [];
    }
  }

  const provider = getWalletProviderById(loginProvider);
  const accounts = await readEthAccounts(provider);
  let address = accounts[0] || normalizeAddress(getWalletAddress());

  if (!address) {
    try {
      const profile = await getUserProfile();
      address = pickWalletFromProfile(profile);
    } catch (err) {
      console.warn('[wallet-address] getUserProfile failed', err);
    }
  }

  if (!address) return [];
  return [{ address, providerId: loginProvider, addressType: 1 }];
}

/** @deprecated 使用 resolveLoginWalletTargets */
export async function discoverUserWalletTargets() {
  return resolveLoginWalletTargets();
}

/**
 * 余额只读查询用的地址解析（跳过 Privy 切链 / iframe ping，优先本地缓存）
 */
export async function resolveFastWalletTargetsForBalance() {
  const loginProvider = normalizeWalletProviderId(getWalletProviderId());
  const cached = normalizeAddress(getWalletAddress());

  if (cached) {
    return [
      {
        address: cached,
        providerId: loginProvider === 'privy' ? 'privy' : loginProvider,
        addressType: loginProvider === 'privy' ? 2 : 1,
      },
    ];
  }

  if (isPrivyEmbeddedLogin()) {
    try {
      const user = await getPrivySessionUser();
      const { address } = extractPrivyTradeAddressFields(user);
      if (address) {
        return [{ address, providerId: 'privy', addressType: 2 }];
      }
    } catch (err) {
      console.warn('[wallet-address] fast privy address failed', err);
    }
  } else {
    const provider = getWalletProviderById(loginProvider);
    const accounts = await readEthAccounts(provider);
    const address = accounts[0];
    if (address) {
      return [{ address, providerId: loginProvider, addressType: 1 }];
    }
  }

  return resolveLoginWalletTargets();
}
